import { addLeadingSlash, getComponent, loadAsyncComponents } from "./common";
import OrderMatrixLink from "./order-matrix-link";
import VariantSelector from "./variant-selector";
import ColorVariantPopupLink from "./color-variant-popup-link";
import ProductInquiryFormLink from "./product-inquiry-link";
import PriceMatrixLink from "./price-matrix-link";
import Cart from "./cart";
import ProductCount from "./product-count";
import ImageGallery from "./image-gallery";
import ProductAvailabilityTable from "./product-availability-table";
import ProductPrintingOptions from "./product-printing-options"
import StockDetailsPopupLink from "./stock-detail-popup-link";
import EnterCalculatorLink from "./enter-calculator-link"
import TronCalculator from "./tron-calculator";
import { initTronDesignerProductDetailLink, initTronDesigner } from "./tron-designer/tron-designer";
import { ActionCreator, AjaxDispatcher } from "./ajax-dispatcher";
import $ from "jquery";
import PrintingOnlineDemand from './printing-online-demand';
import BuyProductLink from './buy-product-link';
import { syncPageHead } from './page-head-sync';

let variantSelector: VariantSelector;

async function initOrderMatrixLink(): Promise<void> {
    const link = document.querySelector('#open-order-matrix-link') as HTMLAnchorElement;
    if (!link) {
        return;
    }
    const linkComponent = new OrderMatrixLink(link, addProductsToCart, openCalculator);
    await linkComponent.init();
}

function initImageGallery(): void {
    new ImageGallery('[data-fancybox="images"]', '#fancybox-more');
}

function initEnterCalculationLink(): void {
    const baseElement = document.querySelector('[data-component-part="enter-calculator-control"]') as HTMLElement;
    if (baseElement) {
        new EnterCalculatorLink(baseElement, openCalculator);
    }
}

function initPriceMatrixLink(): void {
    const link = document.querySelector('[data-component-part="open-price-matrix-link"]') as HTMLAnchorElement;
    if (!link) {
        return;
    }
    const linkComponent = new PriceMatrixLink(link);
    linkComponent.init();
}

function initVariantSelectors(): void {
    variantSelector = new VariantSelector(getProductDetailMainElement(), loadProductPage);
    variantSelector.init();
}

function initProductInquiryForm(): void {
    const linkElement = document.querySelector('[data-component-part="contact-us-link"]') as HTMLAnchorElement;
    if (!linkElement) {
        return;
    }
    const link = new ProductInquiryFormLink(linkElement);
    link.init();
}

function initColorVariantPopupLink(): void {
    const linkElement = document.querySelector('[data-component-part="all-color-variants-link"]') as HTMLElement;
    if (!linkElement) {
        return;
    }
    const link = new ColorVariantPopupLink(linkElement, loadProductPage);
    link.init();
}

function initStockDetailsPopupLink(): void {
    const linkElements = [...document.querySelectorAll('[data-component-part="stock-details-popup-link"]')];
    linkElements.forEach(element => {
        const linkElement = element as HTMLElement;
        linkElement && new StockDetailsPopupLink(element as HTMLElement);
    });
}

// Loads every still-deferred component of the product page with ONE batched request
// (previously each component issued its own request, each paying the full server context
// resolution). Components rendered inline by the server (no data-load-async) skip the request
// entirely. The post-load wiring runs in the same relative order as the old sequential flow:
// availability table → order controls → printing options.
async function loadDeferredProductComponentsAsync(): Promise<void> {
    const detailElement = getProductDetailElement();
    const availabilityTableElements = [...document.querySelectorAll('[data-component="ProductDetailAvailabilityTable"]')].map(e => e as HTMLElement);
    const orderControlsElement = document.querySelector('[data-component="ProductDetailOrderControls"]') as HTMLElement;
    const stockInfoElement = document.querySelector('[data-component="ProductDetailStockInfo"]') as HTMLElement;
    const printingOptionsElement = detailElement ? getComponent(detailElement, ProductPrintingOptions.componentName) : null;

    // Component wrappers are constructed on the placeholder elements BEFORE the swap — the printing
    // options constructor reads its dataset values from the element it is given (pre-existing
    // behaviour, do not change the construction point).
    const availabilityTables = availabilityTableElements.map(e => new ProductAvailabilityTable(e));
    const printingOptions = printingOptionsElement ? new ProductPrintingOptions(printingOptionsElement) : null;

    const elementsToLoad: HTMLElement[] = [...availabilityTableElements];
    if (orderControlsElement && orderControlsElement.dataset.loadAsync) {
        elementsToLoad.push(orderControlsElement);
    }
    if (stockInfoElement && stockInfoElement.dataset.loadAsync) {
        elementsToLoad.push(stockInfoElement);
    }
    if (printingOptionsElement) {
        elementsToLoad.push(printingOptionsElement);
    }

    // productId/pageId hints: the server rebuilds the product context by primary key and uses the
    // cached page tree directly instead of re-running the code/page resolution (verified server-side;
    // any mismatch falls back to URL-based resolution). Keep in sync with the _Layout prefetch script.
    const hintParams = [];
    const productId = tryGetProductId();
    const pageId = document.body.dataset.pageId;
    if (productId) {
        hintParams.push({ key: 'productId', value: productId.toString() });
        if (pageId) {
            hintParams.push({ key: 'pageId', value: pageId });
        }
    }

    const replaced = await loadAsyncComponents(elementsToLoad, hintParams);

    availabilityTableElements.forEach((element, i) => availabilityTables[i].initLoaded(replaced.get(element)));

    if (orderControlsElement) {
        await initLoadedOrderControls();
    }

    printingOptions && printingOptions.initLoaded(replaced.get(printingOptionsElement));
}

// Wiring that must run once the order-controls markup is in the DOM (inline-rendered or just swapped in).
async function initLoadedOrderControls(): Promise<void> {
    //All prices popup for all model-variants
    initPriceMatrixLink();

    //order control buttons for model-variants (buy without or with printing)
    await initOrderMatrixLink();
    initEnterCalculationLink();
    initTronDesigner();
    initTronDesignerProductDetailLink();

    let detailMainElement= getProductDetailMainElement();
    if(!detailMainElement) {
        throw 'ProductDetailMain not found';
    }

    //for single product?
    new BuyProductLink(detailMainElement); //quantity edit inputs (increment/decrement ...)

    //Demand for both product types
    new PrintingOnlineDemand(detailMainElement);
}


function loadProductPage(pageUrl: string, addToHistory = true): Promise<void> {
    const url = addLeadingSlash(pageUrl);

    // Metadata + OpenGraphTags render the head of the variant URL; they have no element to replace and are
    // copied into <head> by syncPageHead.
    const action = new ActionCreator().reloadComponents(['ProductDetailMain', 'ProductDetailData', 'Breadcrumbs', 'Metadata', 'OpenGraphTags'], url);
    action.successAction = async (serverDom?: Document) => {
        if (addToHistory) {
            window.history.pushState(new ProductPageState(getProductId()), '', url);
        }
        syncPageHead(serverDom);
        syncLanguageSelectorProduct();
        await initProductDetail();
    };

    new AjaxDispatcher().dispatchGetAction(action);
    return Promise.resolve();
}

// The language switch asks the server for the URL of the product shown now, not the one first loaded.
function syncLanguageSelectorProduct(): void {
    const productId = tryGetProductId();
    if (!productId) {
        return;
    }
    document.querySelectorAll('[data-component="LanguageSelector"]')
        .forEach(element => element.setAttribute('data-product-id', productId.toString()));
}

function initProductDetailData() {
    $('.toggle-tabs-init ul li span:first').addClass('selected');
    $('.toggle-tabs-init .tab-content-wrapper').hide();
    $('.toggle-tabs-init .tab-content-wrapper:first').show();

    $('.toggle-tabs-init .tabs-nav__button').click(function () {
        var t = $(this).attr('id');
        $('.toggle-tabs-init .tabs-nav__button').removeClass('selected');
        $('.toggle-tabs-init .tab-content-wrapper').hide();
        $(this).addClass('selected');
        $('#' + t + 'C').fadeIn('slow');
    });
}

function getProductDetailElement(): HTMLElement {
    return getComponent(document.body, 'ProductDetail');
}

export function getProductDetailMainElement(): HTMLElement {
    return getComponent(document.body, 'ProductDetailMain');
}

export const getProductId = () => Number(getProductDetailMainElement().dataset.productId);
export const getParentId = () => Number(getProductDetailMainElement().dataset.parentId);

export const tryGetProductId = () => {
    const idString = getProductDetailMainElement()?.dataset.productId;
    return parseInt(idString);
};

export const getProductModelGuid = () => getProductDetailMainElement().dataset.modelGuid;
export const getProductGuid = () => getProductDetailMainElement().dataset.productGuid;

export const getProductSupplierGuid = () => getProductDetailMainElement().dataset.supplierGuid;
export const getProductDimension1 = () => getProductDetailMainElement().dataset.dim1;
export const getProductDimension2 = () => getProductDetailMainElement().dataset.dim2;

async function addProductsToCart(productCounts: ProductCount[]): Promise<void> {
    const cart = new Cart();
    await cart.addProduct(productCounts);
    await cart.openCartDialog(productCounts);
}

export async function openCalculator(productCounts: ProductCount[], positionId: string = null, technologyId: string = null): Promise<void> {
    const calculator = new TronCalculator();
    if (productCounts && productCounts.length > 0) {
        await calculator.openFromMatrix(getParentId(), getProductModelGuid(), getProductSupplierGuid(), productCounts, technologyId, positionId);
    } else {
        await calculator.openForProduct(getParentId(), getProductModelGuid(), getProductSupplierGuid(), getProductDimension1(), getProductDimension2())
    }
}

export async function initProductDetail() {
    // 1. Initialize UI immediately — no AJAX, no waiting
    initImageGallery();
    initVariantSelectors();
    initColorVariantPopupLink();
    initProductDetailData(); // Tabs must init early so the page doesn't flash unstyled

    // 2. ONE batched request for everything still deferred. Order controls / stock info render
    //    inline on a warm stock cache, so often only the availability table + printing options
    //    (or nothing at all) actually load here.
    await loadDeferredProductComponentsAsync();

    // 3. Init links that depend on the (possibly ajax-loaded) order controls
    initProductInquiryForm();
    initStockDetailsPopupLink();
}

export async function loadProductVariant(productPageState: ProductPageState) : Promise<void> {
    if (!variantSelector || !productPageState || productPageState.productId === getProductId()) {
        return;
    }
    await loadProductPage(window.location.pathname, false);
}

export class ProductPageState {
    productId: number;
    constructor(productId) {
        this.productId = productId;
    }
}





